/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { useMemo, useState } from 'react';
import { Container, Box, Text, Section, Link, Flex } from '@tishman/components';
import { useLocation } from '@reach/router';
import {
  EmailShareButton,
  EmailIcon,
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TwitterIcon,
} from 'react-share';
import * as Icons from '@tishman/icons';

import type { ComponentPropsWithoutRef } from 'react';

interface StoryShareBlockProps {
  data: Queries.SanityStoryQuery;
  showBack?: boolean;
  isLeftSideMenu?: boolean;
}

const StoryShareBlock = (
  props: StoryShareBlockProps & ComponentPropsWithoutRef<typeof Section>,
): React.JSX.Element => {
  const { data, showBack, isLeftSideMenu } = props;

  const storyShareProps = useMemo(() => {
    if (!data?.story?.titleAndSlug?.title)
      throw new Error('Expected valid Story title');
    if (!data?.story?.excerpt) throw new Error('Expected valid Story excerpt');
    return {
      title: data.story.titleAndSlug.title,
      excerpt: data.story.excerpt ?? '',
    };
  }, [data]);

  const { href } = useLocation();

  const [copied, setCopied] = useState(false);

  const onShareLinkClick = () => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const el = document.createElement('input');
      el.value = window.location.href;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);

      setTimeout(() => setCopied(false), 1500);
    }
  };

  const iconHoverSx = {
    '&:hover': {
      path: {
        fill: 'GOLD',
      },
    },
  };

  const [isHovered, setIsHovered] = useState(false);

  return (
    <Section
      data-section="StoryShare"
      {...props}
      sx={{ py: 0, borderTop: isLeftSideMenu ? 'none' : '1px solid black' }}
    >
      <Container
        px={[null, 0]}
        sx={{
          textAlign: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            mt: isLeftSideMenu ? [3, 6] : 3,
            mb: 3,
            width: isLeftSideMenu
              ? '100%'
              : ['267px', null, null, null, '353px'],
          }}
        >
          <Text
            sx={{
              textAlign: isLeftSideMenu === true ? 'start' : 'center',
            }}
            variant="storyLeftMenuHeading"
          >
            Share
          </Text>
          <Flex
            sx={{
              justifyContent: isLeftSideMenu === true ? 'flex-start' : 'center',
              alignItems: 'center',
              ml: isLeftSideMenu === true ? '-16px' : 'initial',
            }}
          >
            <Flex
              onClick={onShareLinkClick}
              sx={{
                width: '64px',
                height: '64px',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              <Icons.CopyShareLink
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                  height: '32px',
                  width: '33px',
                  color: 'text',
                  cursor: 'pointer',
                  stroke: isHovered ? 'GOLD' : 'initial',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -45,
                  left: 0,
                  opacity: copied === true ? 1 : 0,
                  backgroundColor: '#000000',
                  borderRadius: '10px',
                  padding: '2px',
                  width: '100px',
                }}
              >
                <Text sx={{ color: '#FFFFFF' }}>Link copied to clipboard</Text>
              </Box>
            </Flex>
            <EmailShareButton
              subject={`Check this out: ${storyShareProps.title}`}
              url={href}
            >
              <EmailIcon
                bgStyle={{ fill: 'none' }}
                iconFillColor="#000000"
                sx={iconHoverSx}
              />
            </EmailShareButton>
            <FacebookShareButton hashtag="#rockcenter" url={href}>
              <FacebookIcon
                bgStyle={{ fill: 'none' }}
                iconFillColor="#000000"
                sx={iconHoverSx}
              />
            </FacebookShareButton>
            <TwitterShareButton
              hashtags={['rockcenter']}
              title={storyShareProps.title}
              url={href}
              via="@rockcenternyc"
            >
              <TwitterIcon
                bgStyle={{ fill: 'none' }}
                iconFillColor="#000000"
                sx={iconHoverSx}
              />
            </TwitterShareButton>
          </Flex>
        </Box>
        {showBack && (
          <Box sx={{ mt: [5, null, null, null, 6], mb: 5 }}>
            <Link
              href="/magazine/"
              sx={{ display: 'inline-block' }}
              variant="buttonBorder"
            >
              Back to all Stories
            </Link>
          </Box>
        )}
      </Container>
    </Section>
  );
};

export default StoryShareBlock;
